import type { DependencyList, MutableRefObject } from 'react';

export function useRefs<T extends MyRecord>(values: T) {
  const refs = useRef<T>(values);
  refs.current = values;
  return refs;
}

export function useParentRef<T>(parentRef: MutableRefObject<T> | undefined) {
  const ref = useRef<T>();
  return parentRef || ref;
}

export function useFields<T extends MyRecord>(fields: T, deps: DependencyList = []) {
  const { t } = useTranslation();

  const cacheFunctionRef = useRef<MyRecord>({});

  const fields_ = useMemo(() => {
    const cache: MyRecord = {};
    return new Proxy(fields, {
      get(target, p: any) {
        if (p in cache) {
          return cache[p];
        }

        const cacheFunction = cacheFunctionRef.current;
        if (p in cacheFunction) {
          return cacheFunction[p];
        }

        let value = target[p];

        if (typeof value === 'function') {
          value = value.bind(target);
          cacheFunction[p] = value;
        } else {
          cache[p] = value;
        }

        return value;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, t, ...deps]);
  return fields_;
}
